// ─────────────────────────────────────────────
//  NewsTab.js — Tab 4: Management & News
// ─────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { fetchNews, fetchOverview } from "../../config/api";
import "./NewsTab.css";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const diff = Date.now() - d.getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 60)  return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days  < 30)  return `${days}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function NewsTab({ symbol, exchange }) {
  const [news,    setNews]    = useState([]);
  const [overview,setOverview]= useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const [newsResult, overviewResult] = await Promise.allSettled([
          fetchNews(symbol, exchange),
          fetchOverview(symbol, exchange),
        ]);
        if (!cancelled) {
          if (newsResult.status === "fulfilled") {
            const raw = newsResult.value;
            const articles = raw?.news || raw?.articles || (Array.isArray(raw) ? raw : []);
            setNews(articles.slice(0, 12));
          }
          if (overviewResult.status === "fulfilled") setOverview(overviewResult.value);
          if (newsResult.status === "rejected" && overviewResult.status === "rejected")
            setError("Could not load news data.");
        }
      } catch (e) {
        if (!cancelled) setError("Failed to load news.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [symbol, exchange]);

  if (loading) return <NewsSkeleton />;
  if (error)   return <div className="nt-error">⚠ {error}</div>;

  return (
    <div className="nt-wrap">
      {/* ── Management Info ── */}
      {overview && (
        <div className="nt-mgmt-section">
          <div className="nt-section-label">Management &amp; Company</div>
          <div className="nt-mgmt-grid">
            {overview.company_ceo && (
              <MgmtCard icon="👤" label="CEO / MD" value={overview.company_ceo} />
            )}
            {overview.company_employees && (
              <MgmtCard icon="👥" label="Employees" value={Number(overview.company_employees).toLocaleString("en-IN")} />
            )}
            {overview.company_website && (
              <MgmtCard icon="🌐" label="Website" value={overview.company_website} isLink />
            )}
            <MgmtCard icon="🏦" label="Exchange" value={exchange} />
          </div>

          {/* Promoter holding placeholder */}
          <div className="nt-promoter-placeholder">
            <div className="nt-promo-item">
              <span className="nt-promo-label">Promoter Holding</span>
              <span className="nt-promo-val">Data via BSE filings</span>
            </div>
            <div className="nt-promo-item">
              <span className="nt-promo-label">FII Holdings</span>
              <span className="nt-promo-val">Data via NSE filings</span>
            </div>
            <div className="nt-promo-item">
              <span className="nt-promo-label">DII Holdings</span>
              <span className="nt-promo-val">Data via NSE filings</span>
            </div>
          </div>
        </div>
      )}

      {/* ── News Feed ── */}
      <div className="nt-section-label">Latest News</div>
      {news.length === 0 ? (
        <div className="nt-no-news">
          <span>📰</span>
          <p>No recent news found for {symbol}</p>
        </div>
      ) : (
        <div className="nt-news-list">
          {news.map((article, i) => {
            const title     = article.title || article.headline || article.summary || "No title";
            const source    = article.source || article.publisher || article.provider || "";
            const url       = article.url    || article.link || "#";
            const published = article.published_at || article.datetime || article.date || article.publishedAt || "";
            const sentiment = article.sentiment || guessSentiment(title);

            return (
              <a
                key={i}
                className={`nt-article ${sentiment}`}
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                <div className="nt-article-meta">
                  <span className="nt-source">{source}</span>
                  <span className="nt-time">{timeAgo(published)}</span>
                  {sentiment !== "neutral" && (
                    <span className={`nt-sentiment-badge ${sentiment}`}>
                      {sentiment === "positive" ? "▲ Positive" : "▼ Negative"}
                    </span>
                  )}
                </div>
                <div className="nt-title">{title}</div>
                {article.summary && article.summary !== title && (
                  <div className="nt-snippet">
                    {article.summary.slice(0, 160)}{article.summary.length > 160 ? "…" : ""}
                  </div>
                )}
              </a>
            );
          })}
        </div>
      )}

      {/* ── Corporate Actions placeholder ── */}
      <div className="nt-corp-actions">
        <div className="nt-section-label" style={{ margin: 0 }}>Corporate Actions</div>
        <div className="nt-corp-grid">
          {MOCK_ACTIONS.map((a, i) => (
            <div key={i} className="nt-corp-card">
              <span className="nt-corp-icon">{a.icon}</span>
              <span className="nt-corp-type">{a.type}</span>
              <span className="nt-corp-note">{a.note}</span>
            </div>
          ))}
        </div>
        <p className="nt-corp-note-text">
          Live corporate actions (dividends, splits, bonus) will be available once exchange APIs are integrated.
        </p>
      </div>
    </div>
  );
}

function MgmtCard({ icon, label, value, isLink }) {
  return (
    <div className="nt-mgmt-card">
      <span className="nt-mgmt-icon">{icon}</span>
      <div>
        <div className="nt-mgmt-label">{label}</div>
        {isLink
          ? <a className="nt-mgmt-link" href={value} target="_blank" rel="noreferrer">{value}</a>
          : <div className="nt-mgmt-value">{value}</div>
        }
      </div>
    </div>
  );
}

function guessSentiment(title) {
  const t = (title || "").toLowerCase();
  const pos = ["profit", "growth", "record", "surge", "gain", "beat", "strong", "rise", "high", "up", "boost", "positive", "dividend", "buyback"];
  const neg = ["loss", "fall", "drop", "decline", "weak", "miss", "concern", "risk", "down", "cut", "probe", "fraud", "penalty"];
  if (pos.some((w) => t.includes(w))) return "positive";
  if (neg.some((w) => t.includes(w))) return "negative";
  return "neutral";
}

const MOCK_ACTIONS = [
  { icon: "💰", type: "Dividend",    note: "Integrate exchange API in Phase 5" },
  { icon: "✂", type: "Stock Split",  note: "Historical data via CDSL/NSDL" },
  { icon: "🎁", type: "Bonus Issue", note: "Sourced from BSE corporate actions" },
  { icon: "🔄", type: "Buyback",     note: "SEBI filing data required" },
];

function NewsSkeleton() {
  return (
    <div className="nt-wrap">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="nt-article-skel">
          <div className="nt-skel-line w20" />
          <div className="nt-skel-line w90" style={{ height: 16, marginTop: 6 }} />
          <div className="nt-skel-line w70" style={{ height: 12, marginTop: 4 }} />
        </div>
      ))}
    </div>
  );
}
